import {GetInteractionHandlersOptions} from '../index';
import {isEqual} from 'lodash';
import {
    trackFixedLayerIds,
    waypointFixedLayerIds
} from '../../getSyncMapboxStyle/fixedLayerIds';

let idleTimeout: number | undefined;

export default {

    getCursor: (interaction: MovingTrackMapInteraction) => {
        if (interaction.subtype === 'appendingPoint') {
            return 'crosshair';
        } else if (interaction.subtype === 'hoveringPoint') {
            return 'grab';
        } else if (interaction.subtype === 'draggingPoint') {
            return 'grabbing';
        } else if (interaction.subtype === 'hoveringLine') {
            return 'grab';
        } else {
            return 'default';
        }
    },

    onMouseDown: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        onInteractionChange,
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {

        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            onInteractionChange({...interaction, subtype: 'clickingPoint'});
        } else if (interaction.subtype === 'hoveringLine') {
            event.preventDefault();
            onInteractionChange({
                ...interaction,
                subtype: 'clickingLine',
                trackLocalId: interaction.trackLocalId,
            });
        }
    },

    onMouseUp: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        onInteractionChange,
        trackDispatcher,
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {

        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            onInteractionChange({...interaction, subtype: 'hoveringPoint', lngLat: event.lngLat});
        } else if (interaction.subtype === 'clickingLine') {
            event.preventDefault();

            const pointLocalId = trackDispatcher({
                type: 'createPoint',
                trackLocalId: interaction.trackLocalId,
                lngLat: event.lngLat,
            });
            onInteractionChange({
                ...interaction,
                pointLocalId,
                subtype: 'clickingPoint',
            });
        }
    },

    onMouseMove: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        onInteractionChange,
        tracks,
        trackDispatcher,
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {
        const map = event.target;

        if (idleTimeout) clearTimeout(idleTimeout);
        idleTimeout = window.setTimeout(() => {
            onInteractionChange(currentInteraction => {
                if (currentInteraction.subtype === 'appendingPoint') {
                    return {...currentInteraction, idle: true};
                } else {
                    return currentInteraction;
                }
            });
        }, 750);

        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            onInteractionChange({...interaction, subtype: 'draggingPoint'});
            const track = tracks[interaction.trackLocalId];
            if (!track.isGeometryLoaded) return;
            trackDispatcher({
                type: 'updatePoint',
                point: {
                    ...track.points[interaction.pointLocalId],
                    geometry: {
                        type: 'Point',
                        coordinates: event.lngLat.toArray()
                    }
                }
            });

        } else if (interaction.subtype === 'clickingLine') {
            const {toPointLocalId, trackLocalId} = interaction;

            const pointLocalId = trackDispatcher({
                type: 'createPoint',
                lngLat: event.lngLat,
                insertBeforePointLocalId: toPointLocalId,
                trackLocalId,
            });
            onInteractionChange({
                ...interaction,
                pointLocalId,
                subtype: 'draggingPoint',
                trackLocalId: interaction.trackLocalId,
            });
        } else {
            const features = map.queryRenderedFeatures(event.point, {
                layers: [
                    trackFixedLayerIds.localUser.point,
                    trackFixedLayerIds.localUser.normal,
                    trackFixedLayerIds.localHighlight.point,
                    trackFixedLayerIds.localHighlight.normal,
                    waypointFixedLayerIds.localHighlight.pin,
                    waypointFixedLayerIds.localUser.pin,
                ].filter(id => map.getLayer(id)),
                filter: ['==', ['get', 'trackLocalId'], interaction.trackLocalId]
            });

            const feature = features[0];

            if (!feature) {
                onInteractionChange({
                    ...interaction,
                    subtype: 'appendingPoint',
                    idle: false,
                    cursorLngLat: event.lngLat,
                });

            } else if (feature.geometry.type === 'Point') {
                onInteractionChange({
                    ...interaction,
                    pointLocalId: (feature as any as TrackPoint).properties.pointLocalId,
                    subtype: 'hoveringPoint',
                    lngLat: event.lngLat,
                });

            } else {
                onInteractionChange({
                    ...interaction,
                    toPointLocalId: (feature as any as TrackLine).properties.toPointLocalId,
                    subtype: 'hoveringLine',
                    lngLat: event.lngLat,
                });
            }
        }
    },

    onMouseOut: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        onInteractionChange
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {
        if (interaction.subtype === 'appendingPoint') {
            onInteractionChange({...interaction, cursorLngLat: undefined});
        }
    },

    onClick: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        trackDispatcher,
        onInteractionChange,
        onPopupChange,
        popup
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {
        if (interaction.subtype === 'appendingPoint') {
            const pointLocalId = trackDispatcher({
                type: 'createPoint',
                trackLocalId: interaction.trackLocalId,
                lngLat: event.lngLat,
            });
            onInteractionChange({
                ...interaction,
                pointLocalId,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });

        } else if (interaction.subtype === 'hoveringPoint') {
            const nextPopup: MapPopup = {
                type: 'point',
                trackLocalId: interaction.trackLocalId,
                pointLocalId: interaction.pointLocalId,
                mapboxLayerId: interaction.mapboxLayerId
            };
            onPopupChange(isEqual(popup, nextPopup) ? undefined : nextPopup);
        } else if (interaction.subtype === 'clickingPoint') {
            onInteractionChange({
                ...interaction,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        }
    },

    onDblClick: (event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent, {
        interaction,
        trackDispatcher
    }: GetInteractionHandlersOptions<MovingTrackMapInteraction>) => {
        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            trackDispatcher({
                type: 'removePoint',
                pointLocalId: interaction.pointLocalId,
                trackLocalId: interaction.trackLocalId
            });
        }
    }
};
